import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingList } from '@/components/shopping/ShoppingList';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/ui/loading';
import {
  ShoppingBag,
  AlertCircle,
  Calendar,
  Plus,
} from 'lucide-react';
import { useShoppingList, useCurrentWeekMealPlan } from '@/hooks';
import { formatWeekRange } from '@/utils/dateUtils';
import { PageHeader } from '@/components/ui/page-header';

export function ShoppingListPage() {
  const { mealPlanId } = useParams<{ mealPlanId: string }>();
  const navigate = useNavigate();

  // Get current week meal plan if no specific mealPlanId provided
  const {
    data: currentMealPlan,
    isLoading: currentMealPlanLoading,
  } = useCurrentWeekMealPlan();

  // Use provided mealPlanId or current week's meal plan ID
  const activeMealPlanId = mealPlanId || currentMealPlan?.id;

  const {
    data: shoppingList,
    isLoading,
    isError,
    error,
    refetch,
  } = useShoppingList(activeMealPlanId || '');

  // Handle share
  const handleShare = async () => {
    if (!shoppingList) return;

    const shareData = {
      title: 'Lista de Compras - MyMeals',
      text: `Mi lista de compras para la semana del ${formatWeekRange(new Date(shoppingList.weekStart))}`,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (_e) {
        console.log('Share canceled or failed');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Enlace copiado al portapapeles');
    }
  };

  // Loading state
  if (isLoading || currentMealPlanLoading) {
    return (
      <>
        <PageHeader title="Lista de Compras" icon={<ShoppingBag className="h-5 w-5" />} />
        <div className="container mx-auto px-4 py-6 max-w-2xl">
          <Card>
            <CardContent className="py-12 text-center">
              <LoadingSpinner className="mx-auto mb-4" />
              <p className="text-gray-600">Generando lista de compras...</p>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // No meal plan available
  if (!activeMealPlanId) {
    return (
      <>
        <PageHeader title="Lista de Compras" icon={<ShoppingBag className="h-5 w-5" />} />
        <div className="container mx-auto px-4 py-6 max-w-2xl">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-8 w-8 text-blue-500" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">No hay plan semanal</h1>
              <p className="text-gray-600 mb-6">
                Para generar una lista de compras, primero necesitas crear un plan de comidas para
                esta semana.
              </p>
              <Button onClick={() => navigate('/meal-planner')}>
                <Plus className="h-4 w-4 mr-2" />
                Crear plan semanal
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // Error state
  if (isError || !shoppingList) {
    return (
      <>
        <PageHeader title="Lista de Compras" icon={<ShoppingBag className="h-5 w-5" />} />
        <div className="container mx-auto px-4 py-6 max-w-2xl">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-8 w-8 text-orange-500" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">
                No se pudo generar la lista
              </h1>
              <p className="text-gray-600 mb-6">
                {error?.message || 'No hay recetas en el plan semanal o ocurrió un error.'}
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button variant="outline" onClick={() => navigate('/meal-planner')}>
                  Ir al planificador
                </Button>
                <Button onClick={() => refetch()}>Intentar de nuevo</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <PageHeader title="Lista de Compras" icon={<ShoppingBag className="h-5 w-5" />} />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <ShoppingList
          shoppingList={shoppingList}
          onRefresh={() => refetch()}
          onShare={handleShare}
          isLoading={isLoading}
        />
      </div>
    </>
  );
}
